import { ColumnId, CustomerCustomsStatus } from "../../gql/graphql";
import {
  getImportExportSettingsFromMinimalBooking,
  getImportExportSettingsFromRoute,
} from "../../use-cases/import-export/util/ImportExportSettingsGetters";
import { hasSenderReceiverCountries } from "../update/utils/hasSenderReceiverCountries";
import type { MinimalBooking } from "./minimalBooking";

export type BookingFields = Set<ColumnId>;

export const getFieldsToShow = (
  minimalBooking: MinimalBooking,
  isCash: boolean,
): BookingFields => {
  const { route, vehicleType } = minimalBooking;

  const columns: BookingFields = new Set<ColumnId>();

  columns.add(ColumnId.PaymentStatus);
  columns.add(ColumnId.Status);
  columns.add(ColumnId.QuayStatus);
  columns.add(ColumnId.SpecialProperty);
  columns.add(ColumnId.EstimatedPickupTime);
  columns.add(ColumnId.BookingNo);
  columns.add(ColumnId.RouteCode);
  columns.add(ColumnId.CreatedBy);
  columns.add(ColumnId.ModifiedBy);
  columns.add(ColumnId.DateUpdated);
  columns.add(ColumnId.DateCreated);
  columns.add(ColumnId.CustIndex);
  columns.add(ColumnId.DaysOnQuay);
  columns.add(ColumnId.ETA);
  columns.add(ColumnId.DepartureDate);
  columns.add(ColumnId.DepartureTime);

  columns.add(ColumnId.VehicleTypeCode);

  columns.add(ColumnId.Length);
  columns.add(ColumnId.Width);
  columns.add(ColumnId.Height);

  if (
    getImportExportSettingsFromMinimalBooking(
      minimalBooking,
      getImportExportSettingsFromRoute(minimalBooking.route, "importReference"),
    )
  ) {
    columns.add(ColumnId.ImportReference);
  }
  if (
    getImportExportSettingsFromMinimalBooking(
      minimalBooking,
      getImportExportSettingsFromRoute(minimalBooking.route, "exportReference"),
    )
  ) {
    columns.add(ColumnId.ExportReference);
  }

  if (vehicleType?.useTrailerReg) {
    columns.add(ColumnId.TrailerRegNo);
  }
  if (vehicleType?.useVehicleReg) {
    columns.add(ColumnId.VehicleRegNo);
  }

  if (vehicleType?.useTradeWeight) {
    columns.add(ColumnId.TradeWeight);
  }

  if (vehicleType?.useCargoWeight) {
    if (isCash) {
      columns.add(ColumnId.CargoWeight);
      columns.add(ColumnId.Goods);

      if (
        route?.useSenderReceiverCountry &&
        hasSenderReceiverCountries(minimalBooking)
      ) {
        columns.add(ColumnId.ReceiverCountryCode);
        columns.add(ColumnId.SenderCountryCode);
      }
    } else if (route?.useCargoWeight) {
      columns.add(ColumnId.CargoWeight);
      columns.add(ColumnId.Goods);
    }
  }

  if (vehicleType?.useNoOfDrivers) {
    columns.add(ColumnId.NoOfDrivers);
  }

  if (vehicleType?.useHazardous && route?.useHazardousGoods) {
    columns.add(ColumnId.HazardousGoods);
  }

  if (vehicleType?.useAnimals && route?.useLivestockType) {
    columns.add(ColumnId.LivestockTypeCode);
  }

  if (vehicleType?.useTemperature) {
    columns.add(ColumnId.Temperature);
  }

  columns.add(ColumnId.CustomerReference);

  columns.add(ColumnId.LoadingPreferenceCode);

  columns.add(ColumnId.LoadingListMessage);

  if (route?.useCustomsClassification) {
    columns.add(ColumnId.CustomsClassification);
  }

  if (minimalBooking.showDaysOnQuay) {
    columns.add(ColumnId.DaysOnQuay);
  }

  if (
    minimalBooking.customerCustomsStatus &&
    minimalBooking.customerCustomsStatus !== CustomerCustomsStatus.NotApplicable
  ) {
    columns.add(ColumnId.CustomerCustomsStatus);
  }

  return columns;
};

export const isHidden = <T, TFields extends Set<T>>(
  fields: TFields,
  column: T,
) => {
  return !fields.has(column);
};

export const isVisible = <T, TFields extends Set<T>>(
  fields: TFields,
  column: T,
) => {
  return !isHidden(fields, column);
};

export const isAnyVisible = <T, TFields extends Set<T>>(
  fields: TFields,
  columns: T[],
) => {
  return columns.some((column) => isVisible(fields, column));
};
